/* DJ Vodka — theme behaviour. No dependencies. */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------
     Nav drawer
     ------------------------------------------------------------------ */

  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    if (!drawer || drawer.dataset.bound === 'true') return;
    drawer.dataset.bound = 'true';

    var panel = drawer.querySelector('.drawer__panel');
    var openers = document.querySelectorAll('[data-drawer-open]');
    var closers = drawer.querySelectorAll('[data-drawer-close]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      // Force a frame so the transition runs from the off-canvas start state.
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
      });
      document.body.classList.add('drawer-open');
      openers.forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
        b.classList.add('is-open');
      });
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      openers.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        b.classList.remove('is-open');
      });
      document.removeEventListener('keydown', onKeydown);

      // Only actually hide if the drawer is still closed by the time the
      // transition ends — a quick close-then-reopen must not hide the
      // drawer that the second click just opened.
      var done = function () {
        panel.removeEventListener('transitionend', done);
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      };
      panel.addEventListener('transitionend', done);
      // Fallback for browsers that skip the transition (reduced motion).
      setTimeout(done, 450);

      if (lastFocused) lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;

      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    openers.forEach(function (b) {
      b.addEventListener('click', function () {
        drawer.classList.contains('is-open') ? close() : open();
      });
    });
    closers.forEach(function (b) {
      b.addEventListener('click', close);
    });
  }

  /* ------------------------------------------------------------------
     Age gate

     Stored per browser. `sessionStorage` would re-prompt on every tab,
     which reads as broken; a dated localStorage entry that expires after
     30 days is the usual compromise for AU alcohol sites.
     ------------------------------------------------------------------ */

  var AGE_KEY = 'djv:age-ok';
  var AGE_TTL_DAYS = 30;

  function ageVerified() {
    try {
      var raw = localStorage.getItem(AGE_KEY);
      if (!raw) return false;
      var age = (Date.now() - parseInt(raw, 10)) / 86400000;
      return age >= 0 && age < AGE_TTL_DAYS;
    } catch (e) {
      // Private mode / storage blocked — gate every visit rather than skip it.
      return false;
    }
  }

  function initAgeGate() {
    var gate = document.querySelector('[data-age-gate]');
    if (!gate || gate.dataset.bound === 'true') return;
    gate.dataset.bound = 'true';

    if (ageVerified()) return;

    gate.hidden = false;
    document.body.classList.add('age-locked');

    var confirmBtn = gate.querySelector('[data-age-confirm]');
    var denyBtn = gate.querySelector('[data-age-deny]');
    var denied = gate.querySelector('[data-age-denied]');

    if (confirmBtn) confirmBtn.focus();

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        try {
          localStorage.setItem(AGE_KEY, String(Date.now()));
        } catch (e) {
          /* not fatal — they just get asked again next visit */
        }
        gate.hidden = true;
        document.body.classList.remove('age-locked');
      });
    }

    if (denyBtn && denied) {
      denyBtn.addEventListener('click', function () {
        denied.hidden = false;
        denyBtn.disabled = true;
      });
    }

    // Keep focus inside the gate while it is up.
    document.addEventListener('focusin', function (e) {
      if (!gate.hidden && !gate.contains(e.target)) {
        e.stopPropagation();
        if (confirmBtn) confirmBtn.focus();
      }
    });
  }

  /* ------------------------------------------------------------------
     Equaliser

     Bar heights animate via CSS keyframes (see theme.css). Two things have
     to happen in JS: the mirrored per-bar timings, and the ghost bars,
     which follow the coloured bars rather than running their own clock.
     ------------------------------------------------------------------ */

  var GHOST_EXAGGERATION = 2.0;      // ghost target = bar height x 2
  var GHOST_RELEASE_PX_PER_SEC = 384; // fast, just behind the bar's own fall (scaled with the 20% slowdown)

  var eqRafId = null;

  function initEqualizers() {
    var nodes = document.querySelectorAll('[data-eq]');
    if (!nodes.length) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var groups = [];

    Array.prototype.forEach.call(nodes, function (eq) {
      var bars = eq.querySelectorAll('.eq__bar');
      var ghosts = eq.querySelectorAll('.eq__ghost');
      if (!bars.length) return;

      if (eq.dataset.bound !== 'true') {
        eq.dataset.bound = 'true';

        // Mirror timings around the centre bar so the row rises and falls as
        // one symmetric waveform instead of a flicker: bar i shares its
        // duration and delay with bar (count - 1 - i).
        var count = bars.length;
        var centre = (count - 1) / 2;
        var timings = new Array(count);

        for (var i = 0; i <= centre; i++) {
          // 20% slower than the handoff's 1.6-3.0s (client feedback).
          var dur = +(2.0 + Math.random() * 1.75).toFixed(2);
          var delay = +(Math.random() * -dur).toFixed(2);
          timings[i] = { dur: dur, delay: delay };
          timings[count - 1 - i] = timings[i];
        }

        var animate = !eq.classList.contains('eq--static') && !reduceMotion;
        Array.prototype.forEach.call(bars, function (bar, i) {
          if (!animate) { bar.style.animation = 'none'; return; }
          bar.style.animationDuration = timings[i].dur + 's';
          bar.style.animationDelay = timings[i].delay + 's';
        });
      }

      groups.push({ bars: bars, ghosts: ghosts, heights: new Array(bars.length).fill(0) });
    });

    if (!groups.length) return;

    function sync(dt) {
      groups.forEach(function (g) {
        for (var i = 0; i < g.bars.length; i++) {
          var barPx = g.bars[i].offsetHeight;
          var target = barPx * GHOST_EXAGGERATION;
          var h = g.heights[i];

          if (target >= h) {
            h = target; // fast attack — follow the bar up immediately
          } else {
            h = Math.max(target, h - GHOST_RELEASE_PX_PER_SEC * dt);
          }
          h = Math.max(h, barPx); // hard floor — never shorter than the bar

          g.heights[i] = h;
          g.ghosts[i].style.height = h.toFixed(1) + 'px';
        }
      });
    }

    if (eqRafId !== null) cancelAnimationFrame(eqRafId);

    if (document.querySelector('.eq--static') && groups.length === 1) {
      sync(0);
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sync(0);
      return;
    }

    var last = performance.now();
    (function frame(now) {
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      sync(dt);
      eqRafId = requestAnimationFrame(frame);
    })(last);
  }

  /* ------------------------------------------------------------------
     Hero carousel

     Alternates the hero between its dark deck artwork and a light second
     image, pushing one in as the other slides out, always the same way round.
     The movement itself is CSS (a transform transition keyed on each slide's
     data-pos); this decides which slide is arriving, and sets .hero--second on
     the hero while the second image is the one on its way in or showing and,
     when that image is light, .hero--light on the hero and .hero-is-light on
     the body (the header is outside the hero), which turn the text dark and
     take the equaliser away.

     Left still for visitors who ask for reduced motion: an image that changes
     by itself is the kind of movement that setting is for.
     ------------------------------------------------------------------ */

  function initHeroCarousel() {
    var hero = document.querySelector('[data-hero-carousel]');
    if (!hero || hero.dataset.carouselBound === 'true') return;

    var slides = hero.querySelectorAll('[data-hero-slide]');
    if (slides.length < 2) return;
    hero.dataset.carouselBound = 'true';

    var second = slides[1];

    // Fetch the second image only now, once the page has loaded, so it never
    // competes with the deck artwork for first paint.
    function loadSecond() {
      var src = second.getAttribute('data-bg');
      if (src && !second.style.backgroundImage) second.style.backgroundImage = "url('" + src + "')";
    }
    if (document.readyState === 'complete') loadSecond();
    else window.addEventListener('load', loadSecond);

    slides[0].setAttribute('data-pos', 'active');
    slides[1].setAttribute('data-pos', 'right');

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var seconds = parseInt(hero.getAttribute('data-interval'), 10);
    if (!(seconds > 0)) seconds = 6;
    var secondIsLight = hero.getAttribute('data-light') !== 'false';
    var current = 0;

    // Move a slide to a position with no travel: switch the transition off,
    // force a style flush so the jump is committed, then switch it back on.
    function park(slide, pos) {
      slide.setAttribute('data-instant', '');
      slide.setAttribute('data-pos', pos);
      void slide.offsetWidth;
      slide.removeAttribute('data-instant');
    }

    function advance() {
      loadSecond();
      var outgoing = slides[current];
      var next = (current + 1) % slides.length;
      var incoming = slides[next];

      park(incoming, 'right');                       // start beside the screen, not mid-way
      incoming.setAttribute('data-pos', 'active');   // both now travel together
      outgoing.setAttribute('data-pos', 'left');
      current = next;

      var toSecond = current === 1;
      var light = toSecond && secondIsLight;
      hero.classList.toggle('hero--second', toSecond);
      hero.classList.toggle('hero--light', light);
      document.body.classList.toggle('hero-is-light', light);

      // Once it has gone, park the outgoing slide off the right, ready to
      // come round again. Timed from the CSS rather than transitionend, which
      // never fires if the tab is in the background.
      var ms = parseFloat(getComputedStyle(outgoing).transitionDuration) * 1000 || 1200;
      window.setTimeout(function () { if (slides[current] !== outgoing) park(outgoing, 'right'); }, ms + 150);
    }

    window.setInterval(advance, seconds * 1000);
  }

  /* ------------------------------------------------------------------
     Product gallery

     The photos are already a swipeable scroll-snap strip in CSS; this adds
     previous/next arrows and dots, keeps them in step with the scroll
     position, and lets the arrow keys move between photos. Nothing advances
     by itself. Labels come from the locale file through data attributes.
     ------------------------------------------------------------------ */

  function initProductGallery() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryBound === 'true') return;
      var track = gallery.querySelector('[data-gallery-track]');
      var slides = gallery.querySelectorAll('[data-gallery-slide]');
      if (!track || slides.length < 2) return;
      gallery.dataset.galleryBound = 'true';

      var stage = track.parentElement;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var labelPrev = gallery.getAttribute('data-label-prev') || 'Previous photo';
      var labelNext = gallery.getAttribute('data-label-next') || 'Next photo';
      var labelDots = gallery.getAttribute('data-label-dots') || 'Choose a photo';
      var labelDot = gallery.getAttribute('data-label-dot') || 'Show photo';

      function arrow(dir, label) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gallery__nav gallery__nav--' + (dir < 0 ? 'prev' : 'next');
        b.setAttribute('aria-label', label);
        b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + (dir < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
        b.addEventListener('click', function () { go(current() + dir); });
        return b;
      }

      var prev = arrow(-1, labelPrev);
      var next = arrow(1, labelNext);
      stage.appendChild(prev);
      stage.appendChild(next);

      var dotsBox = document.createElement('div');
      dotsBox.className = 'gallery__dots';
      dotsBox.setAttribute('role', 'group');
      dotsBox.setAttribute('aria-label', labelDots);
      var dots = [];
      slides.forEach(function (_, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'gallery__dot';
        d.setAttribute('aria-label', labelDot + ' ' + (i + 1));
        d.addEventListener('click', function () { go(i); });
        dotsBox.appendChild(d);
        dots.push(d);
      });
      gallery.appendChild(dotsBox);

      function current() {
        var w = track.clientWidth || 1;
        return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
      }

      function go(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      }

      function sync() {
        var i = current();
        prev.disabled = i === 0;
        next.disabled = i === slides.length - 1;
        dots.forEach(function (d, n) {
          if (n === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
      }

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; sync(); });
      }, { passive: true });
      window.addEventListener('resize', sync);

      gallery.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
      });

      sync();
    });
  }

  /* ------------------------------------------------------------------
     Hero tagline

     The tagline must render exactly 30% narrower than the wordmark. A
     guessed font-size only ever approximates that and drifts if the logo
     size or face changes, so measure the real text width and scale by the
     ratio actually needed.
     ------------------------------------------------------------------ */

  function fitTaglineToLogo() {
    var logo = document.querySelector('[data-hero-logo]');
    var tagline = document.querySelector('[data-hero-tagline]');
    if (!logo || !tagline) return;

    var target = logo.getBoundingClientRect().width * 0.7;
    if (target <= 0) return;

    // The tagline is nowrap at a fixed px width, so an over-large target
    // overflows rather than wrapping. Never exceed the space available.
    var available = tagline.parentElement ? tagline.parentElement.clientWidth : 0;
    if (available > 0) target = Math.min(target, available);

    // Reset before measuring, or repeated calls compound the scaling.
    tagline.style.fontSize = '';
    tagline.style.width = 'max-content';

    var natural = tagline.getBoundingClientRect().width;
    if (natural <= 0) { tagline.style.width = ''; return; }

    var size = parseFloat(getComputedStyle(tagline).fontSize);
    tagline.style.fontSize = (size * (target / natural)).toFixed(2) + 'px';
    tagline.style.width = target + 'px';
  }

  var fitTimer = null;
  function scheduleFit() {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(fitTaglineToLogo, 120);
  }

  /* ------------------------------------------------------------------
     Product pack-size shortcuts

     Buttons just set the real quantity field's value — there's no separate
     state to keep in sync, and the form submits that field exactly as it
     would if the shopper had typed into it directly.
     ------------------------------------------------------------------ */

  function initPackSelect() {
    var input = document.querySelector('[data-qty-input]');
    if (!input || input.dataset.bound === 'true') return;

    var buttons = document.querySelectorAll('.pack__btn');
    if (!buttons.length) return;
    input.dataset.bound = 'true';

    function syncFromValue() {
      var current = input.value;
      var matched = false;
      buttons.forEach(function (b) {
        var isMatch = b.dataset.packQty === current;
        b.setAttribute('aria-pressed', String(isMatch));
        if (isMatch) matched = true;
      });
      return matched;
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        input.value = b.dataset.packQty;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        syncFromValue();
      });
    });

    // Typing a custom amount clears the preset highlight unless it happens
    // to match one exactly.
    input.addEventListener('input', syncFromValue);
  }

  /* ------------------------------------------------------------------
     Contact popup

     Fires once the visitor has been on the SITE (not the page) for the
     configured time — elapsed time accumulates in sessionStorage, so
     navigating between pages doesn't restart the clock. Never opens while
     the age gate is up; that has to be answered first.
     ------------------------------------------------------------------ */

  var POPUP_SEEN_KEY = 'djv:contact-popup-done';
  var POPUP_ELAPSED_KEY = 'djv:elapsed-ms';
  var POPUP_SUPPRESS_DAYS = 30;

  function popupSuppressed() {
    try {
      var raw = localStorage.getItem(POPUP_SEEN_KEY);
      if (!raw) return false;
      var days = (Date.now() - parseInt(raw, 10)) / 86400000;
      return days >= 0 && days < POPUP_SUPPRESS_DAYS;
    } catch (e) {
      // Storage blocked — suppress rather than risk nagging on every page.
      return true;
    }
  }

  function suppressPopup() {
    try { localStorage.setItem(POPUP_SEEN_KEY, String(Date.now())); } catch (e) {}
  }

  function initContactPopup() {
    var popup = document.querySelector('[data-contact-popup]');
    if (!popup || popup.dataset.bound === 'true') return;
    popup.dataset.bound = 'true';

    var panel = popup.querySelector('.popup__panel');
    var lastFocused = null;

    function close() {
      popup.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      suppressPopup();
      var done = function () {
        panel.removeEventListener('transitionend', done);
        if (!popup.classList.contains('is-open')) popup.hidden = true;
      };
      panel.addEventListener('transitionend', done);
      setTimeout(done, 400);
      if (lastFocused) lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      if (popup.classList.contains('is-open')) return;
      lastFocused = document.activeElement;
      popup.hidden = false;
      requestAnimationFrame(function () { popup.classList.add('is-open'); });
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener('keydown', onKeydown);
    }

    popup.querySelectorAll('[data-popup-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });

    // Shopify reloads with the success state after a contact submit — show it
    // straight away rather than making them wait out the timer again.
    if (popup.querySelector('[data-popup-success]')) {
      suppressPopup();
      open();
      return;
    }

    if (popupSuppressed()) return;

    var delayMs = (parseInt(popup.dataset.delay, 10) || 30) * 1000;

    function readElapsed() {
      try { return parseInt(sessionStorage.getItem(POPUP_ELAPSED_KEY), 10) || 0; }
      catch (e) { return 0; }
    }

    var elapsed = readElapsed();
    var timer = null;

    function tick() {
      elapsed += 1000;
      try { sessionStorage.setItem(POPUP_ELAPSED_KEY, String(elapsed)); } catch (e) {}
      if (elapsed >= delayMs) {
        clearInterval(timer);
        open();
      }
    }

    function startClock() {
      if (elapsed >= delayMs) { open(); return; }
      timer = setInterval(tick, 1000);
    }

    // Hold the clock until the age gate is answered.
    if (document.body.classList.contains('age-locked')) {
      var confirmBtn = document.querySelector('[data-age-confirm]');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', function () { setTimeout(startClock, 0); });
        return;
      }
    }
    startClock();
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init() {
    // theme.liquid sets this for the homepage; correct it if a hero has been
    // placed on another template, or removed from this one.
    document.body.classList.toggle('has-hero', !!document.querySelector('.hero'));

    initDrawer();
    initAgeGate();
    initEqualizers();
    initTaglineFit();
    initHeroCarousel();
    initProductGallery();
    initPackSelect();
    initContactPopup();
    initRecipeCards();
    initApiForms();
  }

  /* --------------------------------------------------------------------
     Recipe cards (Mix).

     The panel content is cloned out of a <template> on open rather than
     living in the DOM, which keeps the recipe photographs off the page
     until someone asks for one.
     -------------------------------------------------------------------- */
  function makeOverlay(root, opts) {
    var panel = root.querySelector(opts.panel);
    var body = root.querySelector(opts.body);
    var lastFocused = null;

    function close() {
      if (root.hidden) return;
      root.hidden = true;
      document.body.classList.remove(opts.bodyClass);
      document.removeEventListener('keydown', onKeydown);
      body.innerHTML = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(tpl) {
      // A <template> parsed inside an <svg> comes back as a plain SVGElement
      // with no .content — guard so a markup slip degrades to a no-op.
      if (!tpl || !tpl.content) return;
      lastFocused = document.activeElement;
      body.appendChild(tpl.content.cloneNode(true));
      // --icon-colour lives on .rcard__card (set per occasion in Liquid), but
      // .rcard__actions — the Print/Close bar — is a sibling outside .rcard__body,
      // so it can't inherit it. Copy it up to .rcard__panel, the nearest shared
      // ancestor, so the print button's border picks up the same colour.
      var card = body.querySelector('.rcard__card');
      if (card) {
        panel.style.setProperty('--icon-colour', card.style.getPropertyValue('--icon-colour'));
      }
      root.hidden = false;
      document.body.classList.add(opts.bodyClass);
      panel.scrollTop = 0;
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener('keydown', onKeydown);
    }

    root.querySelectorAll(opts.closeSel).forEach(function (b) {
      b.addEventListener('click', close);
    });

    return { open: open, close: close };
  }

  function templateFor(attr, id) {
    var all = document.querySelectorAll('template[' + attr + ']');
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute(attr) === id) return all[i];
    }
    return null;
  }

  function initRecipeCards() {
    var root = document.querySelector('[data-rcard]');
    if (!root || root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    var overlay = makeOverlay(root, {
      panel: '.rcard__panel',
      body: '[data-rcard-body]',
      closeSel: '[data-rcard-close]',
      bodyClass: 'rcard-open'
    });

    document.querySelectorAll('[data-rcard-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        overlay.open(templateFor('data-rcard-tpl', btn.getAttribute('data-rcard-open')));
      });
    });

    var print = root.querySelector('[data-rcard-print]');
    if (print) print.addEventListener('click', function () { window.print(); });
  }

  /* --------------------------------------------------------------------
     Forms API

     Any form carrying data-api-form is taken over here and posted to the
     DJ Vodka forms API instead of to Shopify. Without this script the same
     form submits natively, which is why the markup keeps both a Shopify
     `name` and a `data-api-field` on every input.

     Generic on purpose: Phase 2 adds contact and trade by putting the
     attribute on those forms and pointing it at their route. Nothing here
     knows what a competition is.
     -------------------------------------------------------------------- */

  /* Shopify's `t` filter HTML-escapes what it returns, so an apostrophe reaches
     window.DJV.strings as &#39;. These strings are shown as text, not markup,
     so decode them: a visitor should read "couldn't", not "couldn&#39;t".
     A textarea parses entities without ever creating elements from the string,
     so this is safe whatever the string holds. */
  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s;
    var box = document.createElement('textarea');
    box.innerHTML = s;
    return box.value;
  }

  function str(key, fallback) {
    var s = (window.DJV && window.DJV.strings) || {};
    return decodeEntities(s[key] || fallback || '');
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    // RFC-4122-shaped fallback; only needs to be unique per attempt, not secret.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }

  /* A file input's `accept` list only filters the picker. It is a hint: a
     visitor can switch the picker to "All files", and setting input.files
     skips it altogether. The forms API wants unsupported files turned away in
     the browser before upload, so the same list is enforced here — one list,
     declared once in the markup, doing both jobs.

     This is a convenience (a clear message instead of a failed upload), not a
     security check. Browsers derive file.type from the extension, so a renamed
     file gets past it; the API reads the file's signature and refuses that
     itself. Where the browser reports no type, or a generic one, for a format
     it doesn't recognise, the extension stands in for it. */
  var MIME_BY_EXTENSION = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', pdf: 'application/pdf' };

  function fileTypeAllowed(file, accept) {
    if (!accept) return true;
    var type = (file.type || '').toLowerCase();
    var dot = file.name.lastIndexOf('.');
    var ext = dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
    if ((!type || type === 'application/octet-stream') && MIME_BY_EXTENSION[ext]) {
      type = MIME_BY_EXTENSION[ext];
    }
    return accept.split(',').some(function (token) {
      token = token.trim().toLowerCase();
      if (!token) return false;
      if (token.charAt(0) === '.') return ext === token.slice(1);
      if (token.slice(-2) === '/*') return type.indexOf(token.slice(0, -1)) === 0;
      return type === token;
    });
  }

  function initApiForms() {
    document.querySelectorAll('[data-api-form]').forEach(function (form) {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      var endpoint = form.getAttribute('data-api-form');
      var status = form.querySelector('[data-form-status]');
      var submit = form.querySelector('[data-form-submit]');
      var label = form.querySelector('[data-submit-label]');
      var labelText = label ? label.textContent : '';
      var maxBytes = parseInt(form.getAttribute('data-max-bytes'), 10) || 0;
      var attemptKey = null;   // one key per deliberate attempt; kept across retries

      function clearErrors() {
        form.querySelectorAll('[data-field-error]').forEach(function (el) {
          el.hidden = true;
          el.textContent = '';
        });
        form.querySelectorAll('[data-api-field], [data-api-file]').forEach(function (el) {
          el.removeAttribute('aria-invalid');
        });
        if (status) { status.hidden = true; status.textContent = ''; status.className = 'form-status'; }
      }

      function showFieldError(key, message) {
        var slot = form.querySelector('[data-field-error="' + key + '"]');
        var input = form.querySelector('[data-api-field="' + key + '"], [data-api-file="' + key + '"]');
        if (input) input.setAttribute('aria-invalid', 'true');
        if (slot) { slot.textContent = message; slot.hidden = false; }
        return !!slot;
      }

      function showStatus(message, kind) {
        if (!status) return;
        status.textContent = message;
        status.className = 'form-status form-status--' + (kind || 'error');
        status.hidden = false;
        status.scrollIntoView({ block: 'nearest' });
      }

      function setBusy(on) {
        form.dataset.busy = on ? 'true' : 'false';
        if (submit) submit.disabled = on;
        if (label) label.textContent = on ? str('sending', 'Sending...') : labelText;
      }

      function resetTurnstile() {
        var widget = form.querySelector('.cf-turnstile');
        if (widget && window.turnstile && window.turnstile.reset) {
          try { window.turnstile.reset(widget); } catch (e) { /* already reset */ }
        }
      }

      /* The API takes a website only as https://..., but people type
         "mybar.com.au" or an old http:// address, which the browser's URL check
         or the API then refuses without saying why. Complete it for them; the
         field shows what will be sent. Any other scheme is left for the API to
         refuse. */
      function normaliseUrlFields() {
        form.querySelectorAll('input[type="url"][data-api-field]').forEach(function (el) {
          var v = el.value.trim();
          if (v && !/^https:\/\//i.test(v)) {
            if (/^http:\/\//i.test(v)) v = 'https://' + v.slice(7);
            else if (v.indexOf('://') === -1) v = 'https://' + v.replace(/^\/\//, '');
          }
          if (v !== el.value) el.value = v;
        });
      }

      /* Native constraint validation, surfaced through the same error slots the
         API's field errors use, so both look identical to the visitor. */
      function validateLocally() {
        var ok = true;
        var first = null;
        form.querySelectorAll('[data-api-field], [data-api-file]').forEach(function (el) {
          if (el.checkValidity()) return;
          ok = false;
          if (!first) first = el;
          var key = el.getAttribute('data-api-field') || el.getAttribute('data-api-file');
          showFieldError(key, el.validationMessage);
        });

        var file = form.querySelector('[data-api-file]');
        if (ok && file && file.files && file.files[0] && maxBytes && file.files[0].size > maxBytes) {
          ok = false;
          first = file;
          showFieldError(file.getAttribute('data-api-file'), str('FILE_TOO_LARGE', 'That file is too large.'));
        }

        if (ok && file && file.files && file.files[0] && !fileTypeAllowed(file.files[0], file.getAttribute('accept'))) {
          ok = false;
          first = file;
          showFieldError(file.getAttribute('data-api-file'), str('FILE_NOT_ALLOWED', 'That file type is not accepted.'));
        }

        if (!ok) {
          showStatus(str('error_summary', 'Check the highlighted fields and try again.'));
          if (first) first.focus();
        }
        return ok;
      }

      function collectFields() {
        var fields = {};
        try {
          fields = JSON.parse(form.getAttribute('data-api-static') || '{}');
        } catch (e) { fields = {}; }
        var source = form.getAttribute('data-api-source');
        if (source) fields.source = source;

        form.querySelectorAll('[data-api-field]').forEach(function (el) {
          var key = el.getAttribute('data-api-field');
          if (el.type === 'checkbox') {
            fields[key] = el.checked;
          } else if (el.type === 'number') {
            fields[key] = el.value === '' ? null : Number(el.value);
          } else {
            var value = el.value.trim();
            // Optional text fields left blank are omitted rather than sent as
            // "" — an empty string isn't the same as "not provided" to a
            // field like an HTTPS URL, which would fail format validation on
            // an empty value where absence is fine.
            if (value || el.required) fields[key] = value;
          }
        });

        // Fields the API schema has no slot for (e.g. Trade's business type)
        // get folded into another field as readable "Label: value" lines,
        // rather than silently dropped or sent as keys the API doesn't expect.
        var folds = {};
        form.querySelectorAll('[data-api-append-to]').forEach(function (el) {
          var value = el.value && el.value.trim();
          if (!value) return;
          var target = el.getAttribute('data-api-append-to');
          var label = el.getAttribute('data-api-label') || target;
          (folds[target] = folds[target] || []).push(label + ': ' + value);
        });
        Object.keys(folds).forEach(function (target) {
          var extra = folds[target].join('\n');
          fields[target] = fields[target] ? (fields[target] + '\n\n' + extra) : extra;
        });

        return fields;
      }

      // A file input (data-api-file) means the API expects multipart/form-data
      // with a "fields" JSON part alongside the file — the competition entry
      // contract. No file input means a plain JSON body — the Trade enquiry
      // contract. The two forms this theme posts to the API use different wire
      // formats on purpose; this branch is what makes one function serve both.
      var fileField = form.querySelector('[data-api-file]');

      function buildRequest() {
        var fields = collectFields();
        if (fileField) {
          var body = new FormData();
          body.append('fields', JSON.stringify(fields));
          if (fileField.files && fileField.files[0]) {
            body.append(fileField.getAttribute('data-api-file'), fileField.files[0]);
          }
          return { body: body, headers: {} };
        }
        return {
          body: JSON.stringify(fields),
          headers: { 'Content-Type': 'application/json' }
        };
      }

      function onSuccess(data) {
        var text = decodeEntities(form.getAttribute('data-success-text') || '');
        var ref = data && data.reference;
        var panel = document.createElement('div');
        panel.className = 'form-success';
        panel.setAttribute('role', 'status');

        var p = document.createElement('p');
        p.className = 'form-success__text';
        p.textContent = text;
        panel.appendChild(p);

        if (ref) {
          var r = document.createElement('p');
          r.className = 'form-success__ref';
          r.textContent = str('reference', 'Your reference is') + ' ' + ref;
          panel.appendChild(r);
        }
        // The thank-you panel is far shorter than the form it replaces. In the
        // Trade page's split layout the picture is centred against the whole
        // copy-and-form column, so a shorter column let the (lifted) picture
        // overflow upward and get cut off the top of the page. Keep the form's
        // height in reserve (CSS applies it on wide screens only).
        var wrap = document.createElement('div');
        wrap.className = 'form-success-wrap';
        wrap.style.setProperty('--form-h', form.offsetHeight + 'px');
        wrap.appendChild(panel);
        form.replaceWith(wrap);
        panel.focus && panel.focus();
      }

      function onFailure(res, data) {
        var err = (data && data.error) || {};
        var code = err.code || '';
        var handled = false;

        if (err.fields) {
          Object.keys(err.fields).forEach(function (key) {
            if (showFieldError(key, err.fields[key])) handled = true;
          });
        }

        // A code we have wording for wins; otherwise fall back by status.
        var message = str(code, '');
        if (!message) {
          if (res && res.status === 429) message = str('RATE_LIMITED', '');
          else if (handled) message = str('error_summary', '');
          else message = (err.message || str('generic', ''));
        }
        showStatus(message);

        // Turnstile tokens are single-use; without a reset the retry fails too.
        resetTurnstile();
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (form.dataset.busy === 'true') return;

        clearErrors();
        normaliseUrlFields();
        if (!validateLocally()) return;
        if (!attemptKey) attemptKey = uuid();

        var request = buildRequest();
        var headers = { 'Idempotency-Key': attemptKey };
        Object.keys(request.headers).forEach(function (k) { headers[k] = request.headers[k]; });
        // Multipart requests deliberately get no Content-Type here — the
        // browser adds the boundary itself, and overriding it breaks the
        // upload. The JSON path sets it explicitly above instead.
        var token = form.querySelector('[name="cf-turnstile-response"]');
        if (token && token.value) headers['X-Turnstile-Token'] = token.value;

        setBusy(true);
        fetch(endpoint, { method: 'POST', headers: headers, body: request.body })
          .then(function (res) {
            return res.json().catch(function () { return null; }).then(function (data) {
              return { res: res, data: data };
            });
          })
          .then(function (out) {
            setBusy(false);
            if (out.res.ok) {
              attemptKey = null;
              onSuccess(out.data);
            } else {
              onFailure(out.res, out.data);
            }
          })
          .catch(function () {
            // Network failure: keep attemptKey so a retry is the same attempt
            // and cannot create a second entry.
            setBusy(false);
            showStatus(str('offline', ''));
            resetTurnstile();
          });
      });
    });
  }

  /* Refit whenever the wordmark's own rendered width changes. A window resize
     listener misses the cases that actually matter — the hero being revealed,
     a late webfont, the logo capping at 84vw on a narrow screen — and a single
     measurement at load leaves the tagline stuck at whatever width it saw. */
  function initTaglineFit() {
    var logo = document.querySelector('[data-hero-logo]');
    if (!logo) return;

    fitTaglineToLogo();

    if (logo.dataset.fitBound === 'true' || !window.ResizeObserver) return;
    logo.dataset.fitBound = 'true';
    new ResizeObserver(scheduleFit).observe(logo);
  }

  window.addEventListener('resize', scheduleFit);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleFit);
  }
  window.addEventListener('load', scheduleFit);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
